import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Timer, ChevronRight, Play, Pause, Square, AlertTriangle, Check, Heart } from 'lucide-react';

const MOODS = [
  { key: 'happy', emoji: '😊', label: 'Happy' },
  { key: 'neutral', emoji: '😐', label: 'Neutral' },
  { key: 'stressed', emoji: '😟', label: 'Stressed' },
  { key: 'angry', emoji: '😠', label: 'Angry' },
  { key: 'sad', emoji: '😢', label: 'Sad' },
];

export default function SessionTimer() {
  const [phase, setPhase] = useState('setup'); // setup, running, checkin, summary
  const [timerId, setTimerId] = useState(null);
  const [plannedMinutes, setPlannedMinutes] = useState('60');
  const [sessionBudget, setSessionBudget] = useState('');
  const [lossLimit, setLossLimit] = useState('');
  const [checkinFreq, setCheckinFreq] = useState('30');
  const [elapsed, setElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  const [currentBankroll, setCurrentBankroll] = useState('');
  const [checkinMood, setCheckinMood] = useState('neutral');
  const [checkins, setCheckins] = useState([]);
  const [escalation, setEscalation] = useState(null);
  const [summary, setSummary] = useState(null);
  const [endBankroll, setEndBankroll] = useState('');
  const [saveToStreak, setSaveToStreak] = useState(true);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);
  const checkinTimerRef = useRef(null);
  const nextCheckinRef = useRef(0);

  const planned = parseInt(plannedMinutes) * 60;
  const progress = planned > 0 ? Math.min((elapsed / planned) * 100, 100) : 0;
  const remaining = Math.max(planned - elapsed, 0);

  const formatTime = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const stopTimer = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (checkinTimerRef.current) clearTimeout(checkinTimerRef.current);
  }, []);

  const scheduleCheckin = useCallback(() => {
    const freq = parseInt(checkinFreq) * 60 * 1000;
    checkinTimerRef.current = setTimeout(() => {
      setPhase('checkin');
      setPaused(true);
    }, freq);
    nextCheckinRef.current = Date.now() + freq;
  }, [checkinFreq]);

  useEffect(() => {
    if (phase === 'running' && !paused) {
      intervalRef.current = setInterval(() => {
        setElapsed(prev => {
          const next = prev + 1;
          if (next >= planned) {
            stopTimer();
            setPhase('checkin');
            toast.info('Session time is up!');
          }
          return next;
        });
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [phase, paused, planned, stopTimer]);

  const handleStart = async (e) => {
    e.preventDefault();
    if (!sessionBudget || !lossLimit) { toast.error('Fill in all fields'); return; }
    setLoading(true);
    try {
      const res = await api.post('/tools/session-timer/start', {
        planned_duration_minutes: parseInt(plannedMinutes),
        session_budget: parseFloat(sessionBudget),
        loss_limit: parseFloat(lossLimit),
        checkin_frequency_minutes: parseInt(checkinFreq),
      });
      setTimerId(res.data.timer_id);
      setPhase('running');
      scheduleCheckin();
    } catch { toast.error('Failed to start timer'); }
    finally { setLoading(false); }
  };

  const handleCheckin = async () => {
    if (!currentBankroll) { toast.error('Enter current bankroll'); return; }
    try {
      const res = await api.post('/tools/session-timer/checkin', {
        timer_id: timerId, mood: checkinMood, current_bankroll: parseFloat(currentBankroll),
      });
      setCheckins(prev => [...prev, { mood: checkinMood, bankroll: parseFloat(currentBankroll), pl: res.data.checkin.pl }]);
      if (res.data.escalation?.triggered) setEscalation(res.data.escalation);
      if (res.data.loss_limit_hit) {
        toast.error('Loss limit reached. Session ended.');
        handleEndSession(parseFloat(currentBankroll));
        return;
      }
      setCurrentBankroll('');
      setCheckinMood('neutral');
      setPhase('running');
      setPaused(false);
      scheduleCheckin();
    } catch { toast.error('Check-in failed'); }
  };

  const handleEndSession = async (bankroll) => {
    const finalBankroll = bankroll || parseFloat(endBankroll);
    if (!finalBankroll && finalBankroll !== 0) { toast.error('Enter ending bankroll'); return; }
    stopTimer();
    try {
      const res = await api.post('/tools/session-timer/end', {
        timer_id: timerId, ending_bankroll: finalBankroll, save_to_streak: saveToStreak,
      });
      setSummary(res.data);
      setPhase('summary');
    } catch { toast.error('Failed to end session'); }
  };

  const reset = () => {
    stopTimer();
    setPhase('setup');
    setTimerId(null);
    setElapsed(0);
    setPaused(false);
    setCheckins([]);
    setEscalation(null);
    setSummary(null);
    setEndBankroll('');
  };

  // Setup Phase
  if (phase === 'setup') {
    return (
      <div className="max-w-xl mx-auto px-4 py-8 lg:py-12 pt-20" data-testid="session-timer-page">
        <nav className="flex items-center gap-2 text-sm text-[#64748b] mb-6">
          <Link to="/dashboard" className="hover:text-[#4d7cfe]">Dashboard</Link><ChevronRight className="h-3.5 w-3.5" />
          <span className="text-[#a0a8b8]">Emotional Wellness</span><ChevronRight className="h-3.5 w-3.5" /><span className="text-white">Session Timer</span>
        </nav>
        <div className="flex items-start gap-4 mb-8 animate-fade-in">
          <div className="w-12 h-12 rounded-xl bg-[#4d7cfe]/15 flex items-center justify-center shrink-0"><Timer className="h-6 w-6 text-[#4d7cfe]" /></div>
          <div><h1 className="text-2xl lg:text-[32px] font-bold text-white">Session Timer</h1><p className="text-[#a0a8b8] text-sm mt-1">Real-time session timer with emotional check-ins</p></div>
        </div>
        <div className="ciq-card">
          <form onSubmit={handleStart} className="space-y-5">
            <div>
              <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Planned Session Duration</label>
              <Select value={plannedMinutes} onValueChange={setPlannedMinutes}>
                <SelectTrigger className="h-11 bg-[#1a1f2e] border-white/10 text-white" data-testid="timer-duration-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#252b3b] border-white/10">
                  {[{v:'30',l:'30 minutes'},{v:'60',l:'1 hour'},{v:'120',l:'2 hours'},{v:'180',l:'3 hours'}].map(o =>
                    <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Session Budget</label>
              <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b] text-sm">$</span>
                <input type="number" value={sessionBudget} onChange={e => setSessionBudget(e.target.value)} required min="1" placeholder="100"
                  className="w-full h-11 pl-8 pr-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none" data-testid="timer-budget-input" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Loss Limit (auto-stop)</label>
              <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b] text-sm">$</span>
                <input type="number" value={lossLimit} onChange={e => setLossLimit(e.target.value)} required min="1" placeholder="50"
                  className="w-full h-11 pl-8 pr-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none" data-testid="timer-loss-limit-input" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Check-in Frequency</label>
              <Select value={checkinFreq} onValueChange={setCheckinFreq}>
                <SelectTrigger className="h-11 bg-[#1a1f2e] border-white/10 text-white" data-testid="timer-freq-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#252b3b] border-white/10">
                  {[{v:'15',l:'Every 15 min'},{v:'30',l:'Every 30 min'},{v:'60',l:'Every 60 min'}].map(o =>
                    <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-[#4d7cfe] text-white font-medium text-sm btn-lift disabled:opacity-50" data-testid="timer-start-btn">
              {loading ? 'Starting...' : 'Start Session'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Summary Phase
  if (phase === 'summary' && summary) {
    const pl = summary.profit_loss;
    return (
      <div className="max-w-xl mx-auto px-4 pt-20 pb-12" data-testid="timer-summary">
        <div className="ciq-card text-center animate-slide-up mb-6">
          <p className="text-xs text-[#a0a8b8] uppercase tracking-wider mb-2">Session Complete</p>
          <p className="text-3xl font-bold text-white mb-1">{formatTime(summary.actual_duration * 60)}</p>
          <div className="grid grid-cols-3 gap-3 mt-4 mb-4">
            <div className="p-3 rounded-lg bg-[#1a1f2e]"><p className="text-xs text-[#64748b]">Starting</p><p className="text-sm font-bold text-white">${summary.starting_bankroll}</p></div>
            <div className="p-3 rounded-lg bg-[#1a1f2e]"><p className="text-xs text-[#64748b]">Ending</p><p className="text-sm font-bold text-white">${summary.ending_bankroll}</p></div>
            <div className="p-3 rounded-lg bg-[#1a1f2e]"><p className="text-xs text-[#64748b]">P&L</p><p className={`text-sm font-bold ${pl >= 0 ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>{pl >= 0 ? '+' : ''}${pl}</p></div>
          </div>
          {summary.emotional_journey.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-[#a0a8b8] mb-2">Emotional Journey</p>
              <div className="flex justify-center gap-2">
                {summary.emotional_journey.map((mood, i) => {
                  const m = MOODS.find(x => x.key === mood);
                  return <span key={i} className="text-xl" title={m?.label}>{m?.emoji || '😐'}</span>;
                })}
              </div>
            </div>
          )}
          {summary.saved_to_streak && <p className="text-xs text-[#10b981] mb-3">Saved to Streak Tracker</p>}
          {summary.auto_stopped && <p className="text-xs text-[#ef4444] mb-3">Session was auto-stopped (loss limit reached)</p>}
        </div>
        <div className="flex gap-3">
          <button onClick={reset} className="flex-1 h-10 rounded-lg bg-[#4d7cfe] text-white text-sm font-medium btn-lift">New Session</button>
          <Link to="/viral-tools/betting-streak-tracker" className="flex-1 h-10 rounded-lg border border-white/10 text-[#a0a8b8] text-sm flex items-center justify-center hover:bg-white/5">View History</Link>
        </div>
      </div>
    );
  }

  // Running / Checkin Phase
  return (
    <div className="max-w-xl mx-auto px-4 pt-20 pb-12" data-testid="timer-running">
      {/* Timer Display */}
      <div className="ciq-card text-center mb-6">
        <p className="text-xs text-[#a0a8b8] uppercase tracking-wider mb-1">Session Time</p>
        <p className="text-5xl font-bold text-white font-mono mb-2" data-testid="timer-display">{formatTime(elapsed)}</p>
        <div className="h-2 bg-[#252b3b] rounded-full mb-2">
          <div className={`h-full rounded-full transition-all ${progress > 90 ? 'bg-[#ef4444]' : progress > 70 ? 'bg-[#f59e0b]' : 'bg-[#4d7cfe]'}`} style={{ width: `${progress}%` }} />
        </div>
        <p className="text-xs text-[#64748b]">{formatTime(remaining)} remaining of {plannedMinutes} min</p>
        {checkins.length > 0 && (
          <div className="mt-3 pt-3 border-t border-white/10">
            <p className="text-xs text-[#64748b]">Session P&L</p>
            <p className={`text-lg font-bold ${checkins[checkins.length - 1].pl >= 0 ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>
              {checkins[checkins.length - 1].pl >= 0 ? '+' : ''}${checkins[checkins.length - 1].pl}
            </p>
          </div>
        )}
        <div className="flex justify-center gap-3 mt-4">
          <button onClick={() => setPaused(!paused)} className="h-10 px-6 rounded-lg border border-white/10 text-[#a0a8b8] text-sm hover:bg-white/5 flex items-center gap-2" data-testid="timer-pause-btn">
            {paused ? <><Play className="h-3.5 w-3.5" /> Resume</> : <><Pause className="h-3.5 w-3.5" /> Pause</>}
          </button>
          <button onClick={() => { setEndBankroll(''); setPhase('checkin'); setPaused(true); }} className="h-10 px-6 rounded-lg bg-[#ef4444] text-white text-sm font-medium flex items-center gap-2" data-testid="timer-end-btn">
            <Square className="h-3.5 w-3.5" /> End Session
          </button>
        </div>
      </div>

      {/* Emotional Timeline */}
      {checkins.length > 0 && (
        <div className="ciq-card mb-4">
          <h3 className="text-white font-semibold text-sm mb-2">Check-in History</h3>
          <div className="space-y-1.5">
            {checkins.map((c, i) => {
              const m = MOODS.find(x => x.key === c.mood);
              return (
                <div key={i} className="flex items-center gap-3 text-xs">
                  <span>{m?.emoji}</span>
                  <span className="text-[#a0a8b8]">Check-in #{i + 1}</span>
                  <span className={`ml-auto font-bold ${c.pl >= 0 ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>{c.pl >= 0 ? '+' : ''}${c.pl}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Check-in Dialog */}
      <Dialog open={phase === 'checkin'} onOpenChange={() => {}}>
        <DialogContent className="bg-[#252b3b] border-white/10 max-w-sm" data-testid="checkin-dialog">
          <DialogHeader><DialogTitle className="text-white">{elapsed >= planned ? 'Session Time Up!' : 'Quick Check-In'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-[#a0a8b8] mb-2">How are you feeling?</p>
              <div className="flex gap-2">
                {MOODS.map(m => (
                  <button key={m.key} type="button" onClick={() => setCheckinMood(m.key)}
                    className={`flex-1 h-10 rounded-lg text-center text-lg transition-all ${checkinMood === m.key ? 'bg-white/10 ring-1 ring-white/20 scale-110' : 'bg-[#1a1f2e]'}`}
                    data-testid={`checkin-mood-${m.key}`}>{m.emoji}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-[#a0a8b8] mb-1 block">Current Bankroll</label>
              <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748b] text-sm">$</span>
                <input type="number" value={currentBankroll} onChange={e => setCurrentBankroll(e.target.value)} placeholder={sessionBudget}
                  className="w-full h-10 pl-7 pr-3 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm focus:outline-none" data-testid="checkin-bankroll-input" />
              </div>
            </div>
            {escalation && (
              <div className="p-3 rounded-lg bg-[#ef4444]/5 border border-[#ef4444]/20">
                <p className="text-xs text-[#ef4444] font-medium mb-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {escalation.message}</p>
                {escalation.suggestions.map((s, i) => <p key={i} className="text-xs text-[#a0a8b8] ml-4">- {s}</p>)}
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={handleCheckin} className="flex-1 h-10 rounded-lg bg-[#4d7cfe] text-white text-sm font-medium" data-testid="checkin-continue-btn">Continue</button>
              <button onClick={() => {
                if (currentBankroll) handleEndSession(parseFloat(currentBankroll));
                else { setEndBankroll(''); setPhase('checkin'); }
              }} className="flex-1 h-10 rounded-lg bg-[#ef4444] text-white text-sm font-medium" data-testid="checkin-end-btn">End Session</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
